import { X, User, Bell, Lock, Globe } from "lucide-react";
import { IconButton, Button } from "./ui/button";

interface AccountPreferencesProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  userAvatar?: string;
  onLogout?: () => void;
}

export function AccountPreferences({
  isOpen,
  onClose,
  userName,
  userAvatar,
  onLogout,
}: AccountPreferencesProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--background)] rounded-xl shadow-2xl border border-[var(--border)] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[var(--color-steel)] to-[var(--color-steel-dark)] rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-[var(--color-steel-dark)]" />
            </div>
            <h2 className="text-2xl font-semibold text-[var(--foreground)]">Préférences du compte</h2>
          </div>
          <IconButton
            icon={X}
            variant="ghost"
            onClick={onClose}
            aria-label="Fermer"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Profile Section */}
          <div className="bg-[var(--muted)] rounded-lg p-6 border border-[var(--border)]">
            <h3 className="text-lg font-semibold text-[var(--foreground)] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[var(--color-steel-dark)]" />
              Profil
            </h3>
            <div className="flex items-center gap-4">
              <img
                src={userAvatar}
                alt={userName}
                className="w-20 h-20 rounded-full object-cover border-2 border-[var(--color-steel-dark)]"
              />
              <div className="flex-1">
                <div className="mb-3">
                  <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    defaultValue={userName}
                    className="w-full px-3 py-2 bg-[var(--input-background)] border border-[var(--switch-background)] rounded-lg text-[var(--foreground)] hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    defaultValue="simon@restaurant.com"
                    className="w-full px-3 py-2 bg-[var(--input-background)] border border-[var(--switch-background)] rounded-lg text-[var(--foreground)] hover:border-[var(--color-steel-darker)] focus:border-[var(--color-steel)] focus:ring-[3px] focus:ring-[var(--color-steel-bg)] focus:outline-none transition-all"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="bg-[var(--muted)] rounded-lg p-6 border border-[var(--border)]">
            <h3 className="text-lg font-semibold text-[var(--foreground)] mb-4 flex items-center gap-2">
              <Bell className="w-5 h-5 text-[var(--color-steel-dark)]" />
              Notifications
            </h3>
            <div className="space-y-3">
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-[var(--foreground)]">Alertes de stock faible</span>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-4 h-4 rounded border-[var(--color-steel-dark)]/40 bg-[var(--background)] text-[var(--color-steel-dark)] focus:ring-2 focus:ring-[var(--color-steel-dark)]/50 focus:ring-offset-0 cursor-pointer transition-colors"
                />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-[var(--foreground)]">Notifications de modifications</span>
                <input
                  type="checkbox"
                  defaultChecked
                  className="w-4 h-4 rounded border-[var(--color-steel-dark)]/40 bg-[var(--background)] text-[var(--color-steel-dark)] focus:ring-2 focus:ring-[var(--color-steel-dark)]/50 focus:ring-offset-0 cursor-pointer transition-colors"
                />
              </label>
              <label className="flex items-center justify-between cursor-pointer">
                <span className="text-sm text-[var(--foreground)]">Rappels de commande</span>
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-[var(--color-steel-dark)]/40 bg-[var(--background)] text-[var(--color-steel-dark)] focus:ring-2 focus:ring-[var(--color-steel-dark)]/50 focus:ring-offset-0 cursor-pointer transition-colors"
                />
              </label>
            </div>
          </div>

          {/* Security Section */}
          <div className="bg-[var(--muted)] rounded-lg p-6 border border-[var(--border)]">
            <h3 className="text-lg font-semibold text-[var(--foreground)] mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5 text-[var(--color-steel-dark)]" />
              Sécurité
            </h3>
            <Button
              variant="secondary"
              onClick={() => console.log("Changer le mot de passe")}
              className="w-full"
            >
              Changer le mot de passe
            </Button>
          </div>

          {/* Language Section */}
          <div className="bg-[var(--muted)] rounded-lg p-6 border border-[var(--border)]">
            <h3 className="text-lg font-semibold text-[var(--foreground)] mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[var(--color-steel-dark)]" />
              Langue et région
            </h3>
            <div>
              <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-1">
                Langue
              </label>
              <select className="w-full px-3 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-steel-dark)] focus:border-transparent">
                <option>Français</option>
                <option>English</option>
                <option>Español</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[var(--border)]">
          {onLogout && (
            <Button
              variant="destructive"
              onClick={() => { onLogout(); onClose(); }}
            >
              Déconnexion
            </Button>
          )}
          <div className="flex items-center gap-3 ml-auto">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Annuler
            </Button>
            <Button
              variant="default"
              onClick={onClose}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}